import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import useEmblaCarousel from 'embla-carousel-react';
import { ChevronLeft, ChevronRight, Download, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useGetPostsQuery } from '@/redux/post/post.api.slice';
import ProfilePicture from '@/components/ui/profile-picture';
import LikeButton from '@/redux/like/LikeButton';
import { Skeleton } from '@/components/ui/skeleton';
import OnefinityOfficialIcon from '@/assets/onefinity_officials_badge.png';
import { IPostWithUserAndAttachments } from '@/redux/post/post';
import { filterAttachments } from '@/redux/post/post.utils';
import { cn } from '@/lib/utils';

function FeaturedCarouselCard({ post }: { post: IPostWithUserAndAttachments }) {
  const filteredAttachments = filterAttachments(post.attachments);
  const totalAttachmentsDownloadCount = filteredAttachments.reduce(
    (count, attachment) => count + (attachment.downloadCount || 0),
    0
  );
  const totalDownloadCount = Math.round(
    totalAttachmentsDownloadCount / filteredAttachments.length
  );
  return (
    <Link
      key={post._id}
      to={`/model/${post._id}`}
      className="flex-[0_0_280px] sm:flex-[0_0_745px] min-w-0"
    >
      <div className="h-[410px] sm:h-[380px] w-full shadow-md flex flex-col justify-between sm:justify-center items-stretch sm:items-center transition-all duration-300 scale-95 border-gradient border-[1.5px] rounded-lg overflow-hidden">
        <div className="flex justify-between items-center sm:gap-10 flex-col-reverse sm:flex-row overflow-hidden ">
          <div className="w-[280px] sm:w-[300px] flex flex-col gap-5 h-full px-4 sm:px-0 justify-between py-4 sm:py-0">
            <div className="flex flex-col gap-5 h-full">
              <Link to={`/${post.user.username}`}>
                <div className="flex gap-3 items-center order-1 sm:order-2">
                  <ProfilePicture
                    className="sm:w-[36px] sm:h-[36px] w-6 h-6"
                    src={post.user.profilePicture}
                    alt={post.user.firstname}
                  />

                  <div className="flex gap-2 items-center">
                    <h6 className="font-primary font-medium">
                      @{post.user.username}
                    </h6>
                    <div className="flex gap-2 items-center">
                      {post.user.isOnefinityOfficial ? (
                        <img
                          src={OnefinityOfficialIcon}
                          alt="onefinity official"
                          className="h-6 w-6"
                        />
                      ) : (
                        // eslint-disable-next-line react/jsx-no-useless-fragment
                        <></>
                      )}
                      {post.partner ? (
                        <div
                          className={cn(
                            `h-6 w-6 rounded-full flex items-center justify-center`
                          )}
                          style={{
                            backgroundColor: post.partner?.color,
                          }}
                        >
                          <Star className="h-3 w-3 text-white" fill="white" />
                        </div>
                      ) : (
                        // eslint-disable-next-line react/jsx-no-useless-fragment
                        <></>
                      )}
                    </div>
                  </div>
                </div>
              </Link>
              <h5 className="text-base sm:text-xl font-bold leading-none font-primary capitalize order-2 sm:order-1 truncate">
                {post.title}
              </h5>

              <p className="text-sm text-[#666666] font-tertiary font-normal sm:line-clamp-6 line-clamp-2 order-3 sm:order-3">
                {post.description}
              </p>
            </div>
            <div className="flex gap-3 sm:border-none sm:pt-0 border-t border-[#EDEDED] pt-4">
              <LikeButton
                likes={post?.likes}
                liked={post?.hasLikedByUser}
                postId={post._id}
              />
              <Button variant="outline" size="sm">
                <div className="flex items-center gap-1 justify-center">
                  <Download size="14px" />
                  <p>Downloads</p>
                  <p>{totalDownloadCount}</p>
                </div>
              </Button>
            </div>
          </div>
          <img
            src={
              post.attachments.filter(
                (attachment) => attachment.resourceType === 'image'
              )[0]?.url
            }
            alt="avatar"
            className="h-[240px] sm:h-[272px] w-full sm:w-[272px] object-contain"
          />
        </div>
      </div>
    </Link>
  );
}

function FeaturedCarousel() {
  const { data: posts, isLoading } = useGetPostsQuery({
    page: 1,
    limit: 5,
  });
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: 'center',
    slidesToScroll: 1,
    // breakpoints: {
    //   '(min-width: 768px)': {
    //     slidesToScroll: 1,
    //   },
    // },
  });

  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
  }, [emblaApi, onSelect]);

  if (posts?.length === 0 || isLoading) {
    return (
      <div className="flex flex-col items-center gap-8 w-full max-w-[calc(280px*3)] sm:max-w-[calc(745px*3)] mx-auto">
        <div className="w-full">
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex gap-4 pr-[calc(50%-372.5px)]">
              {Array.from({ length: 3 }).map((_, index) => (
                <div
                  className="flex-[0_0_280px] sm:flex-[0_0_745px] min-w-0"
                  key={index}
                >
                  <Skeleton className="h-[410px] sm:h-[380px] w-full overflow-hidden" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-8 w-full max-w-[calc(280px*3)] sm:max-w-[calc(745px*3)] mx-auto">
      <div className="w-full">
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex gap-4 pr-[calc(50%-372.5px)]">
            {posts?.map((post) => (
              <FeaturedCarouselCard key={post._id} post={post} />
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-8 w-full justify-between">
        <Button
          variant="outline"
          size="icon"
          onClick={scrollPrev}
          className="rounded-full"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        <div className="flex space-x-2">
          {posts?.map((post, index) => {
            return (
              <button
                key={post._id}
                type="button"
                aria-label={`Go to slide ${index + 1}`}
                className={`h-2 w-2 rounded-full transition-colors ${
                  index === selectedIndex ? 'bg-primary' : 'bg-gray-300'
                }`}
                onClick={() => emblaApi?.scrollTo(index)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    emblaApi?.scrollTo(index);
                  }
                }}
              />
            );
          })}
        </div>

        <Button
          variant="outline"
          size="icon"
          onClick={scrollNext}
          className="rounded-full"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

export default FeaturedCarousel;
